import React, { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import ScannerLogin from './ScannerLogin';
import ScanPage from './ScanPage';
import HistoryPage from './HistoryPage';
import SettingsPage from './SettingsPage';
import { clearScannerSession, getScannerToken, getScannerUser } from './scannerApi';

const THEME = '#0d6efd';
const SIDEBAR_W = 240;

const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

// The scanner PWA: own login, own session, and a sidebar with only "Scan QR" and "History".
// Lives at /scanner/*, outside the admin dashboard layout.
const ScannerApp = () => {
  const [loggedIn, setLoggedIn] = useState(!!getScannerToken());
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  // Install the scanner on its own: point the page at the scanner manifest while this app is open
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const meta = document.querySelector('meta[name="theme-color"]');
    const prev = { href: link?.getAttribute('href'), color: meta?.getAttribute('content') };
    link?.setAttribute('href', '/scanner.webmanifest');
    meta?.setAttribute('content', THEME);

    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    const onLogout = () => setLoggedIn(false);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    window.addEventListener('scanner-logout', onLogout);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      window.removeEventListener('scanner-logout', onLogout);
      if (prev.href) link?.setAttribute('href', prev.href);
      if (prev.color) meta?.setAttribute('content', prev.color);
    };
  }, []);

  if (!loggedIn) return <ScannerLogin onLoggedIn={() => setLoggedIn(true)} />;

  const user = getScannerUser();
  const install = async () => {
    installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
  };
  const logout = () => {
    clearScannerSession();
    setLoggedIn(false);
  };
  const link = ({ isActive }) => `d-flex align-items-center gap-3 px-4 py-3 text-decoration-none ${isActive ? 'bg-primary text-white' : 'text-dark'}`;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9' }}>
      <style>{`
        .sc-side { position: fixed; top: 0; bottom: 0; left: 0; width: ${SIDEBAR_W}px; z-index: 1040; background: #fff;
          border-right: 1px solid #dee2e6; display: flex; flex-direction: column; transform: translateX(-100%); transition: transform .2s; }
        .sc-side.open { transform: none; }
        .sc-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 1035; }
        .sc-main { padding-bottom: env(safe-area-inset-bottom); }
        @media (min-width: 768px) {
          .sc-side { transform: none; }
          .sc-main { margin-left: ${SIDEBAR_W}px; }
          .sc-burger, .sc-backdrop { display: none; }
        }
      `}</style>

      <aside className={`sc-side ${menuOpen ? 'open' : ''}`}>
        <div className="text-white px-4 py-3 fw-bold fs-5" style={{ background: THEME, paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
          <i className="fa fa-qrcode me-2" />
          Scanner
        </div>
        <nav className="flex-grow-1" onClick={() => setMenuOpen(false)}>
          <NavLink to="/scanner" end className={link}>
            <i className="fa fa-camera" /> Scan QR
          </NavLink>
          <NavLink to="/scanner/history" className={link}>
            <i className="fa fa-history" /> History
          </NavLink>
          <NavLink to="/scanner/settings" className={link}>
            <i className="fa fa-cog" /> Settings
          </NavLink>
        </nav>
        <div className="border-top p-3 small">
          <div className="text-muted text-truncate mb-2">{user?.email}</div>
          {installEvent && !installed && (
            <button className="btn btn-outline-primary btn-sm w-100 mb-2" onClick={install}>
              Install app
            </button>
          )}
          {!installed && !installEvent && isIos() && <div className="text-muted mb-2">To install: tap Share, then “Add to Home Screen”.</div>}
          <button className="btn btn-outline-danger btn-sm w-100" onClick={logout}>
            Logout
          </button>
        </div>
      </aside>
      {menuOpen && <div className="sc-backdrop" onClick={() => setMenuOpen(false)} />}

      <div className="sc-main">
        <header className="d-flex align-items-center gap-3 text-white px-3 py-2" style={{ background: THEME, paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}>
          <button className="sc-burger btn btn-link text-white p-0 fs-4" aria-label="Menu" onClick={() => setMenuOpen(true)}>
            <i className="fa fa-bars" />
          </button>
          <strong className="fs-5">PWA Login</strong>
        </header>

        <div className="mx-auto px-3 py-3" style={{ maxWidth: 560 }}>
          {!online && <div className="alert alert-warning">You are offline. Scans need a connection so the status is always current.</div>}
          <Routes>
            <Route index element={<ScanPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/scanner" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default ScannerApp;
